import { ANALYTIC_PERIODS, RAW_STATS_PERIODS } from "./constants"
import { buildSessionStats, computeTotals } from "./analyticsMetrics"
import { computeComparison, formatDateRange } from "@/common/helpers/comparisonMetrics.helper"

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

function safeArray(val) {
  return Array.isArray(val) ? val : []
}


// ---------------------------------------------------------------------------
// Raw dataset accessors (primary)
// ---------------------------------------------------------------------------

export const periodStats = (state) => safeArray(state.datasets.primary.period)

export const deviceStats = (state) => safeArray(state.datasets.primary.device)

export const regionalStats = (state) => safeArray(state.datasets.primary.regional)

export const royaltyStats = (state) => safeArray(state.datasets.primary.royalty)

// ---------------------------------------------------------------------------
// Raw dataset accessors (comparison)
// ---------------------------------------------------------------------------

export const comparisonPeriodStats = (state) => safeArray(state.datasets.comparison.period)

export const comparisonDeviceStats = (state) => safeArray(state.datasets.comparison.device)

export const comparisonRegionalStats = (state) => safeArray(state.datasets.comparison.regional)

// ---------------------------------------------------------------------------
// Params & misc
// ---------------------------------------------------------------------------

export const params = (state) => state.params
export const comparisonParams = (state) => state.comparisonParams
export const cityStats = (state) => state.cityStats

// ---------------------------------------------------------------------------
// Date range labels (used by charts & tables)
// ---------------------------------------------------------------------------

export const dateRangeLabel = (state) => formatDateRange(state.params)

export const comparisonDateRangeLabel = (state) => formatDateRange(state.comparisonParams)

// ---------------------------------------------------------------------------
// Request-state getters
// ---------------------------------------------------------------------------

export const isStreamingAnalyticsRequested = (state) => state.isStreamingAnalyticsRequested
export const periodRequestState = (state) => state.streamingRequestStates.period
export const deviceRequestState = (state) => state.streamingRequestStates.device
export const regionalRequestState = (state) => state.streamingRequestStates.regional
export const royaltyRequestState = (state) => state.streamingRequestStates.royalty

// ---------------------------------------------------------------------------
// Flags
// ---------------------------------------------------------------------------

export const isComparisonMode = (state) => state.isComparisonMode

export const isDataFetched = (state) => {
  const ds = state.datasets.primary
  return ds.period !== null || ds.device !== null || ds.regional !== null || ds.royalty !== null
}

export const isDayAnalytics = (state) => state.periodType === "day"

// ---------------------------------------------------------------------------
// Static / constant getters
// ---------------------------------------------------------------------------

export const analyticPeriods = () => ANALYTIC_PERIODS
export const rawStatsPeriods = () => RAW_STATS_PERIODS

// ---------------------------------------------------------------------------
// Session stats (primary + comparison)
// ---------------------------------------------------------------------------

export const sessionStats = (state, getters) => {
  if (!getters.isDataFetched) return null
  return buildSessionStats(getters.periodStats, getters.isDayAnalytics)
}

export const comparisonSessionStats = (state, getters) => {
  const ds = state.datasets.comparison
  const hasData = ds.period !== null || ds.device !== null || ds.regional !== null || ds.royalty !== null
  if (!hasData) return null
  return buildSessionStats(getters.comparisonPeriodStats, state.comparisonPeriodType === "day")
}

// ---------------------------------------------------------------------------
// Computed totals & comparison metrics
// ---------------------------------------------------------------------------

/** All primary metric totals in one object */
export const primaryTotals = (_, getters) => computeTotals(getters.sessionStats)

/** All comparison metric totals in one object */
export const comparisonTotals = (_, getters) => computeTotals(getters.comparisonSessionStats)

/**
 * Combined metrics with delta / percentChange / trend for every metric key.
 * Returns null when comparison mode is off.
 * Each key: { value, comparisonValue, delta, percentChange, trend }
 */
export const metrics = (state, getters) => {
  if (!state.isComparisonMode) return null
  return computeComparison(getters.primaryTotals, getters.comparisonTotals)
}

// ---------------------------------------------------------------------------
// App stats (no comparison support)
// ---------------------------------------------------------------------------

function sumField(arr, fn) {
  if (!arr || !arr.length) return 0
  return arr.reduce((total, item) => total + fn(item), 0)
}

export const appStats = (state, getters) => {
  if (!getters.isDataFetched) return null

  const sessionStats = {
    timePeriod: [],
    connections: [],
    minutes: [],
    usersIos: [],
    usersAndroid: [],
  }

  const stats = getters.periodStats
  if (!stats || !stats.length) return sessionStats

  for (const entry of stats) {
    sessionStats.timePeriod.push(getters.isDayAnalytics ? entry.date.slice(11) : entry.date)

    if (entry.sessions !== undefined) sessionStats.connections.push(entry.sessions)
    if (entry.minutes !== undefined) sessionStats.minutes.push(entry.minutes)
    if (entry.uniques?.ios !== undefined) sessionStats.usersIos.push(entry.uniques.ios)
    if (entry.uniques?.android !== undefined) sessionStats.usersAndroid.push(entry.uniques.android)
  }

  return sessionStats
}

export const totalAppSessions = (_, getters) => sumField(getters.periodStats, (e) => e.sessions ?? 0)
export const totalAppMinutes = (_, getters) => sumField(getters.periodStats, (e) => e.minutes ?? 0)
export const totalAppUsers = (_, getters) => sumField(getters.periodStats, (e) => e.uniques?.total ?? 0)
export const totalAppUsersAndroid = (_, getters) => sumField(getters.periodStats, (e) => e.uniques?.android ?? 0)
export const totalAppUsersIos = (_, getters) => sumField(getters.periodStats, (e) => e.uniques?.ios ?? 0)
